import { Controller } from "@hotwired/stimulus"
import { THEMES, THEME_DISPLAY_NAMES } from "../shared/constants/themes"

// Manages theme selection via a flyout submenu in the account dropdown.
// Persists choice to cookie (server-readable) + localStorage (fallback) + DB (via API).
//
// Resolution priority:
//   1. Cookie (server reads on every request for SSR)
//   2. localStorage (fallback for first visit before cookie exists)
//   3. Server-rendered data-theme attribute (system default)
//
// FOUC prevention is handled by an inline <script> in <head>
// (application.html.erb) that checks for cookie, falls back to localStorage.

function hexLuminance(hex) {
  const r = parseInt(hex.slice(1, 3), 16) / 255
  const g = parseInt(hex.slice(3, 5), 16) / 255
  const b = parseInt(hex.slice(5, 7), 16) / 255
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

export default class extends Controller {
  static targets = ["menu"]
  static values = { resolved: String }

  connect() {
    this.themeNames = Object.keys(THEMES)
      .sort((a, b) => hexLuminance(THEMES[b].surface.base) - hexLuminance(THEMES[a].surface.base))
    this.buildOptions()

    const theme = this.currentTheme
    this.applyTheme(theme)
    this.updateChecks(theme)

    this.closeMenuBound = this.closeMenu.bind(this)
    document.addEventListener("click", this.closeMenuBound)
  }

  disconnect() {
    document.removeEventListener("click", this.closeMenuBound)
  }

  get currentTheme() {
    // Check cookie first (server-authoritative)
    const cookieMatch = document.cookie.match(/theme_key=([^;]+)/)
    if (cookieMatch && this.themeNames.includes(cookieMatch[1])) return cookieMatch[1]

    // Fall back to localStorage
    const stored = localStorage.getItem("mantraviz-theme")
    if (this.themeNames.includes(stored)) return stored

    // Server-resolved theme from DB (survives Turbo navigation where
    // <html data-theme> is stale because Turbo only replaces <body>)
    if (this.hasResolvedValue && this.themeNames.includes(this.resolvedValue)) return this.resolvedValue

    return document.documentElement.dataset.theme ||
      this.themeNames[Math.floor(Math.random() * this.themeNames.length)]
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()
    this.menuTarget.classList.toggle("hidden")
  }

  select(event) {
    event.preventDefault()
    event.stopPropagation()
    const theme = event.currentTarget.dataset.themeValue
    if (!theme || !this.themeNames.includes(theme)) return

    // 1. Immediate visual update
    this.applyTheme(theme)
    this.updateChecks(theme)
    this.menuTarget.classList.add("hidden")

    // 2. Persist: cookie (server-readable) + localStorage (fallback)
    this.writeCookie(theme)
    localStorage.setItem("mantraviz-theme", theme)

    // 3. Persist to DB (async, non-blocking)
    this.saveToServer(theme)

    // 4. Close parent dropdown
    const details = this.element.closest("details[data-controller='dropdown']")
    if (details) details.removeAttribute("open")

    // 5. Notify canvas and other listeners
    document.dispatchEvent(
      new CustomEvent("theme:changed", { detail: { theme } })
    )
  }

  preview(event) {
    const theme = event.currentTarget.dataset.themeValue
    if (!theme || !this.themeNames.includes(theme)) return
    this.applyTheme(theme)
    document.dispatchEvent(
      new CustomEvent("theme:changed", { detail: { theme } })
    )
  }

  restore() {
    const theme = this.currentTheme
    this.applyTheme(theme)
    document.dispatchEvent(
      new CustomEvent("theme:changed", { detail: { theme } })
    )
  }

  closeMenu(event) {
    if (!this.element.contains(event.target)) {
      this.menuTarget.classList.add("hidden")
      this.restore()
    }
  }

  // -- private --

  applyTheme(theme) {
    document.documentElement.dataset.theme = theme
  }

  writeCookie(theme) {
    const expires = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toUTCString()
    document.cookie = `theme_key=${theme}; path=/; expires=${expires}; SameSite=Lax`
  }

  async saveToServer(theme) {
    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      await fetch("/api/v1/theme", {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": token,
        },
        body: JSON.stringify({ theme_key: theme }),
      })
    } catch (e) {
      // Non-critical — cookie and localStorage ensure UX continuity
      console.warn("Theme save failed:", e)
    }
  }

  buildOptions() {
    this.menuTarget.innerHTML = ""
    this.themeNames.forEach(name => {
      const btn = document.createElement("button")
      btn.type = "button"
      btn.dataset.themeValue = name
      btn.dataset.action = "click->theme#select mouseenter->theme#preview mouseleave->theme#restore"
      btn.className = "flex items-center justify-between w-full px-3 py-2 text-sm " +
        "text-secondary hover:bg-surface-overlay rounded"

      const label = document.createElement("span")
      label.textContent = THEME_DISPLAY_NAMES[name]

      const check = document.createElement("span")
      check.dataset.check = ""
      check.className = "invisible text-accent"
      check.textContent = "\u2713"

      btn.appendChild(label)
      btn.appendChild(check)
      this.menuTarget.appendChild(btn)
    })
  }

  updateChecks(active) {
    this.menuTarget.querySelectorAll("[data-theme-value]").forEach(el => {
      const check = el.querySelector("[data-check]")
      if (!check) return
      check.classList.toggle("invisible", el.dataset.themeValue !== active)
    })
  }
}
